// src/extenders/select-handler.js
var CustomSelect = class extends HTMLElement {
  constructor() {
    super();
    this.triggerButton = this.querySelector("[data-select-trigger]");
    this.selectedTextEl = this.querySelector("#select-label");
    this.listbox = this.querySelector("[data-select-listbox]");
    this.options = [...this.querySelectorAll("[data-select-option]")];
    this.inputs = document.querySelectorAll("#quality-property");
    this.propertySpans = document.querySelectorAll("span[data-property]");
    this.activeIndex = -1;
    this.searchBuffer = "";
    this.searchTimer = null;
    if (!this.triggerButton || !this.listbox || !this.options.length) {
      console.warn("CustomSelect: Required elements not found");
      return;
    }
    this.init();
  }
  /**
   * Initialize event listeners for the custom select component.
   */
  init() {
    this.triggerButton.addEventListener("click", () => this.toggleListbox());
    this.triggerButton.addEventListener("keydown", this.handleTriggerKeyDown.bind(this));
    this.options.forEach((option, index) => {
      option.addEventListener("click", () => this.selectOption(index));
      option.addEventListener("keydown", (event) => this.handleOptionKeyDown(event, index));
      option.addEventListener("mouseover", () => this.setActiveOption(index));
    });
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener("click", this.boundHandleOutsideClick);
    if (this.options.length) {
      this.setActiveOption(0);
    }
  }
  /**
   * Clean up event listeners when component is removed
   */
  disconnectedCallback() {
    if (this.boundHandleOutsideClick) {
      document.removeEventListener("click", this.boundHandleOutsideClick);
    }
  }
  /**
   * Toggle the listbox visibility
   */
  toggleListbox() {
    const isExpanded = this.triggerButton.getAttribute("aria-expanded") === "true";
    if (!isExpanded) {
      this.openListbox();
    } else {
      this.closeListbox();
    }
  }
  /**
   * Open the listbox
   */
  openListbox() {
    this.listbox.classList.remove("hidden");
    this.triggerButton.setAttribute("aria-expanded", "true");
    const activeOption = this.options[this.activeIndex] || this.options[0];
    if (activeOption) {
      activeOption.focus();
    }
  }
  /**
   * Close the listbox
   */
  closeListbox() {
    this.listbox.classList.add("hidden");
    this.triggerButton.setAttribute("aria-expanded", "false");
    this.triggerButton.focus();
  }
  /**
   * Set the active option
   * @param {number} index The index of the option to activate
   */
  setActiveOption(index) {
    if (this.activeIndex > -1 && this.options[this.activeIndex]) {
      this.options[this.activeIndex].setAttribute("aria-selected", "false");
    }
    this.activeIndex = index;
    const option = this.options[index];
    if (option) {
      option.setAttribute("aria-selected", "true");
    }
  }
  /**
   * Select an option and update all related elements
   * @param {number} index The index of the option to select
   */
  selectOption(index) {
    const option = this.options[index];
    if (!option)
      return;
    const value = option.dataset.value;
    if (!value)
      return;
    this.selectedTextEl.textContent = option.textContent;
    this.setActiveOption(index);
    this.inputs.forEach((input) => input.value = value);
    this.propertySpans.forEach((span) => span.textContent = value);
    this.closeListbox();
  }
  /**
   * Handle keydown events on the trigger button
   * @param {KeyboardEvent} event The keyboard event
   */
  handleTriggerKeyDown(event) {
    var _a, _b;
    const key = event.key;
    switch (key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        this.openListbox();
        if (key === "ArrowUp") {
          this.setActiveOption(this.options.length - 1);
          (_a = this.options[this.options.length - 1]) == null ? void 0 : _a.focus();
        } else {
          this.setActiveOption(0);
          (_b = this.options[0]) == null ? void 0 : _b.focus();
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        this.toggleListbox();
        break;
    }
  }
  /**
   * Handle keydown events on option elements
   * @param {KeyboardEvent} event The keyboard event
   * @param {number} index The index of the current option
   */
  handleOptionKeyDown(event, index) {
    const key = event.key;
    switch (key) {
      case "ArrowDown":
        event.preventDefault();
        if (index < this.options.length - 1) {
          this.setActiveOption(index + 1);
          this.options[index + 1].focus();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (index > 0) {
          this.setActiveOption(index - 1);
          this.options[index - 1].focus();
        }
        break;
      case "Home":
        event.preventDefault();
        this.setActiveOption(0);
        this.options[0].focus();
        break;
      case "End":
        event.preventDefault();
        this.setActiveOption(this.options.length - 1);
        this.options[this.options.length - 1].focus();
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        this.selectOption(index);
        break;
      case "Escape":
        event.preventDefault();
        this.closeListbox();
        break;
      default:
        if (key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
          this.handleTypeahead(key);
        }
    }
  }
  /**
   * Handle typeahead search
   * @param {string} char The character typed
   */
  handleTypeahead(char) {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchBuffer += char.toLowerCase();
    const matchIndex = this.options.findIndex(
      (option) => {
        var _a;
        return (_a = option.textContent) == null ? void 0 : _a.toLowerCase().startsWith(this.searchBuffer);
      }
    );
    if (matchIndex >= 0) {
      this.setActiveOption(matchIndex);
      this.options[matchIndex].focus();
    }
    this.searchTimer = setTimeout(() => {
      this.searchBuffer = "";
    }, 500);
  }
  /**
   * Handle clicks outside the component
   * @param {MouseEvent} event The click event
   */
  handleOutsideClick(event) {
    const isExpanded = this.triggerButton.getAttribute("aria-expanded") === "true";
    if (!isExpanded)
      return;
    if (!this.contains(event.target)) {
      this.closeListbox();
    }
  }
};
customElements.define("custom-select", CustomSelect);
